import moment from 'moment';
import {
  HomeSpaceApiItem,
  HomeSpaceEngagementApiItem,
  HomeSpaceProgressApiItem,
} from '@core/types/state';
import { SpaceWidgetCardData } from '@app/components/presentational/Home/widgets/spaceWidget/SpaceWidget.types';

export type HomeSpaceMetaPills = {
  academicYear: string | null;
  dateRange: string | null;
};

const formatMonthDay = (date?: string | null): string => {
  if (!date) {
    return '';
  }

  return moment.utc(date).format('MMM D');
};

export const formatHomeSpaceAcademicYear = (
  startDate?: string | null,
  endDate?: string | null
): string => {
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;

  if (!start && !end) {
    return '';
  }

  const startYear = start?.getFullYear();
  const endYear = end?.getFullYear();

  if (startYear && endYear) {
    return startYear === endYear
      ? `${startYear}`
      : `${startYear} - ${String(endYear).slice(-2)}`;
  }

  return `${startYear || endYear || ''}`;
};

export const formatHomeSpaceDateRange = (
  startDate?: string | null,
  endDate?: string | null
): string => {
  const startLabel = formatMonthDay(startDate);
  const endLabel = formatMonthDay(endDate);

  if (startLabel && endLabel) {
    return `${startLabel} - ${endLabel}`;
  }

  if (startLabel) {
    return `Starts ${startLabel}`;
  }

  if (endLabel) {
    return `Ends ${endLabel}`;
  }

  return '';
};

export const getHomeSpaceMetaPills = (
  startDate?: string | null,
  endDate?: string | null
): HomeSpaceMetaPills => {
  const hasStart = Boolean(startDate);
  const hasEnd = Boolean(endDate);

  if (!hasStart && !hasEnd) {
    return { academicYear: null, dateRange: null };
  }

  if (hasStart && hasEnd) {
    return {
      academicYear: formatHomeSpaceAcademicYear(startDate, endDate) || null,
      dateRange: formatHomeSpaceDateRange(startDate, endDate) || null,
    };
  }

  if (hasEnd) {
    return {
      academicYear: formatHomeSpaceAcademicYear(null, endDate) || null,
      dateRange: `Ends ${formatMonthDay(endDate)}`,
    };
  }

  if (hasStart) {
    return {
      academicYear: formatHomeSpaceAcademicYear(startDate, null) || null,
      dateRange: `Starts ${formatMonthDay(startDate)}`,
    };
  }

  return {
    academicYear: null,
    dateRange: null,
  };
};

const getMaterialDescription = (
  progress?: HomeSpaceProgressApiItem
): string | null => progress?.currentMaterial?.title?.trim() || null;

const getCompletionPercentage = (
  progress?: HomeSpaceProgressApiItem
): number => {
  if (!progress || progress.totalCount <= 0) {
    return 0;
  }

  return Math.round((progress.completedCount / progress.totalCount) * 100);
};

export const mapHomeSpaceToCard = (
  space: HomeSpaceApiItem,
  progress?: HomeSpaceProgressApiItem,
  engagement?: HomeSpaceEngagementApiItem
): SpaceWidgetCardData => {
  const isStudent = space.role === 'student';
  const { academicYear, dateRange } = getHomeSpaceMetaPills(
    space.startDate,
    space.endDate
  );

  return {
    id: space.spaceId,
    title: space.title,
    description: getMaterialDescription(progress),
    imageUrl: space.imageUrl ?? null,
    academicYear,
    dateRange,
    isCommunity: !space.startDate,
    role: space.role,
    completionPercentage: isStudent
      ? getCompletionPercentage(progress)
      : undefined,
    activeStudentsPercentage: isStudent
      ? undefined
      : engagement?.activeStudentsPercentage ?? 0,
    currentMaterialId: progress?.currentMaterial?.id ?? null,
    hasAccessedMaterials: progress?.hasAccessedMaterials ?? false,
  };
};
